/**
 * Interventions Tab — V2 (mockup 2)
 * 3-column layout: intervention list · detail panel · quick actions
 * Reuses the existing ActivityFormModal for "Nouvelle intervention".
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ActivityFormModal from "@/components/activities/ActivityFormModal";
import ActivityDetailModal from "@/components/activities/ActivityDetailModal";
import ActivityDossier from "@/pages/ops/activities/ActivityDossier";
import MissionAffecteeModal from "@/components/activities/MissionAffecteeModal";
import EnseigneLogo from "@/components/EnseigneLogo";
import UserAvatar from "@/components/UserAvatar";
import { useAuth } from "@/lib/auth";
import { ACTIVITY_TYPES } from "@/lib/activityTypes";
import {
  LayoutList, MapPin, Calendar, User as UserIcon, Search, Filter, ChevronRight,
  Plus, MoreHorizontal, Pencil, Check, Clock, AlertTriangle,
  Camera, FileText, Target, ClipboardCheck, Sparkles,
  ChevronLeft, ChevronDown,
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const typeByKey = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.key, t]));
const NATURE_COLORS = {
  visite: "bg-emerald-100 text-emerald-700",
  audit:  "bg-purple-100 text-purple-700",
  formation: "bg-blue-100 text-blue-700",
  entretien: "bg-amber-100 text-amber-700",
  point_equipe: "bg-slate-100 text-slate-700",
  rdv: "bg-indigo-100 text-indigo-700",
  maintenance: "bg-rose-100 text-rose-700",
};
const STATUS_META = {
  completed: { label: "Réalisée", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  in_progress: { label: "En cours", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  planned:   { label: "À venir", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  a_traiter: { label: "À traiter", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  problemes: { label: "Problèmes", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};
const initials = (n) => (n||"?").split(/\s+/).map(x=>x[0]).join("").slice(0,2).toUpperCase();
const Avatar = ({ name, avatarUrl, size=28 }) => (
  <UserAvatar name={name} avatarUrl={avatarUrl} size={size}/>
);
const fmtDate = (d) => { if (!d) return "—"; try { const dt = new Date(d); return dt.toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric"}); } catch { return String(d).slice(0,10); } };
const fmtTime = (d) => { if (!d) return ""; try { const dt = new Date(d); return dt.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}); } catch { return ""; } };

const _classify_nature = (type_key) => {
  if (!type_key) return "visite";
  if (type_key.startsWith("audit")) return "audit";
  if (type_key.startsWith("formation")) return "formation";
  if (type_key.startsWith("entretien")) return "entretien";
  if (type_key === "point_equipe") return "point_equipe";
  if (type_key === "rdv_magasin") return "rdv";
  if (type_key === "maintenance") return "maintenance";
  return "visite";
};

export default function InterventionsTabV2() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState(null);
  const [dossierId, setDossierId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [missionModalOpen, setMissionModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");

  const [f, setF] = useState({ q:"", dr:[], secteur:[], store_id:[], statut:[], nature:[], date_from:"", date_to:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, sec, u, i, p] = await Promise.all([
        api.get("/activities"),
        api.get("/stores"),
        api.get("/sectors"),
        api.get("/users"),
        api.get("/issues").catch(()=>({data:[]})),
        api.get("/action-plans").catch(()=>({data:[]})),
      ]);
      setRows(a.data || []); setStores(s.data || []); setSectors(sec.data || []); setUsers(u.data || []);
      setIssues(i.data || []); setPlans(p.data || []);
    } catch (e) { toast.error("Erreur chargement interventions"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-open form modal when ?new=1
  useEffect(() => {
    if (sp.get("new") === "1") {
      setModalOpen(true);
      sp.delete("new"); setSp(sp, { replace: true });
    }
  }, [sp, setSp]);

  const storesById = useMemo(() => Object.fromEntries(stores.map(s => [s.id, s])), [stores]);
  const drs = useMemo(() => [...new Set(stores.map(s => s.dr_name).filter(Boolean))].sort(), [stores]);
  const secteurs = useMemo(() => sectors.map(s => s.name || s.id), [sectors]);

  const filtered = useMemo(() => {
    let r = rows.slice();
    if (f.q) {
      const q = f.q.toLowerCase();
      r = r.filter(x => (x.visit_type||"").toLowerCase().includes(q)
                     || (x.user_name||"").toLowerCase().includes(q)
                     || (storesById[x.store_id]?.canonical_name || storesById[x.store_id]?.name || "").toLowerCase().includes(q));
    }
    if (f.dr?.length)      r = r.filter(x => f.dr.includes(storesById[x.store_id]?.dr_name || ""));
    if (f.secteur?.length) r = r.filter(x => f.secteur.includes(storesById[x.store_id]?.sector_id || ""));
    if (f.store_id?.length) r = r.filter(x => f.store_id.includes(x.store_id));
    if (f.statut?.length)  r = r.filter(x => f.statut.includes(x.status));
    if (f.nature?.length)  r = r.filter(x => f.nature.includes(_classify_nature(x.type_key)));
    if (f.date_from) r = r.filter(x => (x.date||"") >= f.date_from);
    if (f.date_to)   r = r.filter(x => (x.date||"") <= f.date_to);
    return r.sort((a,b) => String(b.date||"").localeCompare(String(a.date||"")));
  }, [rows, f, storesById]);

  const selected = useMemo(() => filtered.find(r => r.id === selectedId) || filtered[0], [filtered, selectedId]);

  const linkedIssues = useMemo(() => {
    if (!selected) return [];
    return issues.filter(i => i.store_id === selected.store_id).slice(0, 8);
  }, [issues, selected]);
  const linkedPlans = useMemo(() => {
    if (!selected) return [];
    return plans.filter(p => p.corner === (storesById[selected.store_id]?.canonical_name || storesById[selected.store_id]?.name)
                          || p.sector_id === selected.sector_id).slice(0, 8);
  }, [plans, selected, storesById]);

  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="ivt-loading">Chargement des interventions…</div>;

  return (
    <div className="space-y-3" data-testid="ivt-v2-page">
      <div className="flex items-center justify-end gap-2">
        <button onClick={()=>setMissionModalOpen(true)} className="flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-lg bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-50 shadow-sm" data-testid="ivt-new-mission"><Target className="w-3.5 h-3.5"/> Affecter une mission</button>
        <button onClick={()=>setModalOpen(true)} className="flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="ivt-new"><Plus className="w-3.5 h-3.5"/> Nouvelle activité</button>
      </div>

      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-2.5">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <div className="lg:col-span-2 relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={f.q} onChange={e=>setF(v=>({...v,q:e.target.value}))} placeholder="Rechercher…" className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-[12px]" data-testid="ivt-search"/>
          </div>
          <FilterMultiSelect size="sm" menuWidth={230} label="DR" placeholder="Tous les DR" testid="ivt-f-dr" options={drs} value={f.dr} onChange={v=>setF(o=>({...o,dr:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Secteur" placeholder="Tous les secteurs" testid="ivt-f-secteur" options={sectors.map(o=>({value:o.id,label:o.name}))} value={f.secteur} onChange={v=>setF(o=>({...o,secteur:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Corner" placeholder="Tous les corners" testid="ivt-f-corner" options={stores.map(o=>({value:o.id,label:o.canonical_name||o.name}))} value={f.store_id} onChange={v=>setF(o=>({...o,store_id:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Statut" placeholder="Tous les statuts" testid="ivt-f-statut" options={[{value:"completed",label:"Réalisée"},{value:"in_progress",label:"En cours"},{value:"planned",label:"À venir"}]} value={f.statut} onChange={v=>setF(o=>({...o,statut:v}))}/>
          <FilterMultiSelect size="sm" menuWidth={230} label="Nature" placeholder="Toutes natures" testid="ivt-f-nature" options={[{value:"visite",label:"Visite"},{value:"audit",label:"Audit"},{value:"formation",label:"Formation"},{value:"point_equipe",label:"Point équipe"},{value:"rdv",label:"RDV magasin"},{value:"entretien",label:"Entretien"}]} value={f.nature} onChange={v=>setF(o=>({...o,nature:v}))}/>
          <div className="flex items-center gap-1 col-span-2 md:col-span-1 lg:col-span-1">
            <input type="date" value={f.date_from} onChange={e=>setF(v=>({...v,date_from:e.target.value}))} className="w-full px-1.5 py-1.5 rounded-lg border border-slate-200 text-[11px]"/>
            <input type="date" value={f.date_to} onChange={e=>setF(v=>({...v,date_to:e.target.value}))} className="w-full px-1.5 py-1.5 rounded-lg border border-slate-200 text-[11px]"/>
          </div>
        </div>
      </div>

      {/* LIST / MINE / TEAM view — 3 columns */}
      {(
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Left: intervention list */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 flex flex-col" data-testid="ivt-list">
            <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[12px] font-bold text-slate-900">Activités <span className="text-slate-400 font-normal">({filtered.length})</span></h3>
              <button className="text-[10px] text-slate-500 flex items-center gap-1 hover:text-slate-700">Plus récentes <ChevronDown className="w-3 h-3"/></button>
            </div>
            <div className="flex-1 overflow-auto max-h-[760px] scrollbar-thin p-2 space-y-1.5">
              {filtered.length === 0 && <div className="italic text-slate-400 text-[11px] text-center py-8">Aucune intervention pour ces filtres.</div>}
              {filtered.slice(0, 100).map(r => {
                const st = storesById[r.store_id] || {};
                const nat = _classify_nature(r.type_key);
                const status = STATUS_META[r.status] || STATUS_META.completed;
                const active = (selected?.id === r.id);
                const meta = typeByKey[r.type_key];
                return (
                  <button key={r.id} onClick={()=>setSelectedId(r.id)}
                          data-testid={`ivt-card-${r.id}`}
                          className={`w-full text-left rounded-lg border p-2.5 transition ${active?"border-blue-500 bg-blue-50 shadow-sm":"border-slate-200 hover:border-slate-300"}`}>
                    <div className="flex items-start gap-2">
                      <EnseigneLogo enseigne={st.enseigne} size={36}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold text-slate-800 truncate">{st.canonical_name || st.name || "—"}</div>
                        <div className="text-[9px] text-slate-500 truncate">{st.dr_name} · {st.address ? st.address.split(",")[0] : ""}</div>
                      </div>
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full border ${status.cls}`}>{status.label}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-slate-500">
                      <Calendar className="w-3 h-3"/>{fmtDate(r.date)} · {fmtTime(r.created_at)||"—"}
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${NATURE_COLORS[nat]||"bg-slate-100 text-slate-600"}`}>{meta?.label || r.visit_type}</span>
                      <UserAvatar name={r.user_name} avatarUrl={r.user_avatar} size={22}/>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Center: detail panel */}
          {selected ? (
            <div className="lg:col-span-6 bg-white rounded-xl border border-slate-200 flex flex-col" data-testid="ivt-detail">
              <div className="px-4 py-3 border-b border-slate-100 flex items-start gap-3">
                <EnseigneLogo enseigne={storesById[selected.store_id]?.enseigne} size={40}/>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[16px] font-bold text-slate-900 truncate">{storesById[selected.store_id]?.canonical_name || storesById[selected.store_id]?.name || "—"}</h2>
                  <div className="text-[11px] text-slate-500">{storesById[selected.store_id]?.address ? storesById[selected.store_id]?.address.split(",").slice(0,2).join(", ") : ""} · {storesById[selected.store_id]?.dr_name}</div>
                </div>
                <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${(STATUS_META[selected.status]||STATUS_META.completed).cls}`}>{(STATUS_META[selected.status]||STATUS_META.completed).label}</span>
                <button onClick={()=>setDossierId(selected.id)} data-testid="ivt-dossier"
                        className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-[#0B153B] hover:bg-[#15224f] text-white">
                  <ClipboardCheck className="w-3 h-3"/> Dossier
                </button>
                <button onClick={()=>setDetailModal(selected)} data-testid="ivt-edit"
                        className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700">
                  <Pencil className="w-3 h-3"/> Modifier
                </button>
                <button className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"><MoreHorizontal className="w-4 h-4"/></button>
              </div>

              {/* Info tiles */}
              <div className="grid grid-cols-3 gap-2 px-4 py-3 border-b border-slate-100">
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600"/>
                  <div className="min-w-0"><div className="text-[9px] text-slate-500 font-semibold uppercase">Date & durée</div><div className="text-[12px] font-semibold text-slate-800 truncate">{fmtDate(selected.date)}</div><div className="text-[10px] text-slate-500">{selected.duration_min ? `${selected.duration_min} min` : "—"}</div></div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 flex items-center gap-2">
                  <UserIcon className="w-4 h-4 text-blue-600"/>
                  <div className="min-w-0"><div className="text-[9px] text-slate-500 font-semibold uppercase">Responsable</div><div className="text-[12px] font-semibold text-slate-800 truncate">{selected.user_name || "—"}</div><div className="text-[10px] text-slate-500">{user?.role === "director" ? "DR" : "RT"}</div></div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-blue-600"/>
                  <div className="min-w-0"><div className="text-[9px] text-slate-500 font-semibold uppercase">Natures</div><div className="flex flex-wrap gap-1 mt-0.5"><span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${NATURE_COLORS[_classify_nature(selected.type_key)]}`}>{typeByKey[selected.type_key]?.label || selected.visit_type}</span></div></div>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="px-4 flex items-center gap-1 border-b border-slate-100 overflow-x-auto">
                {[["overview","Vue d'ensemble"],["activities","Activités"],["constats","Constats"],["problems","Problèmes"],["plans","Plans d'action"],["missions","Missions"],["documents","Documents"],["history","Historique"]].map(([k,l]) => (
                  <button key={k} onClick={()=>setDetailTab(k)} data-testid={`ivt-subtab-${k}`}
                          className={`px-3 py-2 text-[11px] font-semibold border-b-2 transition whitespace-nowrap ${detailTab===k?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700"}`}>{l}</button>
                ))}
              </div>

              <div className="flex-1 overflow-auto p-4 max-h-[600px]">
                {detailTab === "overview" && (
                  <div className="space-y-4" data-testid="ivt-overview">
                    <div>
                      <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">Résumé de l'intervention</h4>
                      <p className="text-[12px] text-slate-700 leading-relaxed whitespace-pre-line">{selected.description || <span className="italic text-slate-400">Aucun résumé pour cette intervention.</span>}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">Constats clés</h4>
                        <div className="space-y-1.5">
                          {(selected.payload?.constats || [
                            {label:"Qualité produits", desc:"Produits frais, bonne rotation, présentation soignée", ok:true},
                            {label:"Équipe", desc:"Équipe impliquée et dynamique, bon accueil client", ok:true},
                            {label:"Hygiène", desc:"Chambre froide à nettoyer (traces sur joints)", ok:false},
                            {label:"Réassort", desc:"Ruptures en fin de journée sur California saumon", ok:false},
                          ]).slice(0,4).map((c,i) => (
                            <div key={i} className="flex items-start gap-2">
                              <div className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${c.ok?"bg-emerald-100 text-emerald-600":"bg-amber-100 text-amber-600"}`}>{c.ok ? <Check className="w-3 h-3"/> : <AlertTriangle className="w-3 h-3"/>}</div>
                              <div className="min-w-0"><div className="text-[11px] font-semibold text-slate-800">{c.label}</div><div className="text-[10px] text-slate-500 leading-tight">{c.desc}</div></div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">Indicateurs du corner</h4>
                        <div className="space-y-2 text-[11px]">
                          <div><div className="text-slate-500">CA semaine</div><div className="text-[16px] font-bold text-slate-900">12 480 € <span className="text-[10px] text-emerald-600 font-semibold">vs S-1</span></div></div>
                          <div><div className="text-slate-500">Foodcost</div><div className="text-[16px] font-bold text-slate-900">32,4% <span className="text-[10px] text-rose-600 font-semibold">↓ -1,2 pt vs S-1</span></div></div>
                          <div><div className="text-slate-500">Wasascore</div><div className="text-[16px] font-bold text-slate-900">78 <span className="text-[10px] text-slate-500">/100</span> <span className="text-[10px] text-emerald-600 font-semibold">↑ +6 pts</span></div></div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline */}
                    <div>
                      <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-3">Timeline de l'intervention</h4>
                      <div className="flex items-center gap-2 overflow-x-auto">
                        {[
                          {label:"Préparation", d: selected.created_at, done: true},
                          {label:"Sur site",    d: selected.date,       done: true},
                          {label:"Constats",    d: selected.date,       done: selected.status==="completed"||selected.status==="in_progress"},
                          {label:"Plans d'action", d: selected.date,    done: selected.status==="completed"},
                          {label:"Compte rendu", d: selected.date,      done: selected.status==="completed"},
                        ].map((t,i,arr) => (
                          <div key={i} className="flex items-center gap-1 shrink-0">
                            <div className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full grid place-items-center ${t.done?"bg-emerald-100 text-emerald-600":"bg-slate-100 text-slate-400"}`}>{t.done ? <Check className="w-3.5 h-3.5"/> : <Clock className="w-3.5 h-3.5"/>}</div>
                              <div className="text-[10px] font-semibold text-slate-700 mt-1 whitespace-nowrap">{t.label}</div>
                              <div className="text-[9px] text-slate-400">{fmtDate(t.d)}</div>
                            </div>
                            {i<arr.length-1 && <ChevronRight className="w-3 h-3 text-slate-300 mx-1"/>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {detailTab === "activities" && (
                  <div className="text-[12px] text-slate-600 space-y-2">
                    <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-2">Historique des activités du corner</div>
                    {rows.filter(r => r.store_id === selected.store_id).slice(0,10).map(r => (
                      <div key={r.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100">
                        <span className={`w-2 h-2 rounded-full ${r.status==="completed"?"bg-emerald-500":"bg-slate-300"}`}/>
                        <div className="flex-1"><div className="font-semibold text-slate-800 text-[11px]">{typeByKey[r.type_key]?.label || r.visit_type}</div><div className="text-[10px] text-slate-500">{fmtDate(r.date)} · {r.user_name}</div></div>
                        <button onClick={()=>setSelectedId(r.id)} className="text-[10px] text-blue-600 hover:underline">Ouvrir</button>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "constats" && <ConstatsView selected={selected}/>}

                {detailTab === "problems" && (
                  <div className="space-y-2">
                    {linkedIssues.length === 0 && <div className="italic text-slate-400 text-[11px]">Aucun problème signalé sur ce corner.</div>}
                    {linkedIssues.map(i => (
                      <div key={i.id} className="rounded-lg border border-slate-200 p-2.5">
                        <div className="flex items-start gap-2"><div className="w-6 h-6 rounded bg-rose-100 text-rose-600 grid place-items-center shrink-0"><AlertTriangle className="w-3 h-3"/></div>
                          <div className="min-w-0 flex-1"><div className="text-[12px] font-semibold text-slate-800">{i.title}</div><div className="text-[10px] text-slate-500">Signalé par {i.user_name} · {fmtDate(i.created_at)}</div></div>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${i.priority==="critical"?"bg-rose-100 text-rose-700":i.priority==="high"?"bg-orange-100 text-orange-700":"bg-slate-100 text-slate-700"}`}>{i.priority}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "plans" && (
                  <div className="space-y-2">
                    {linkedPlans.length === 0 && <div className="italic text-slate-400 text-[11px]">Aucun plan d'action lié à ce corner.</div>}
                    {linkedPlans.map(p => (
                      <div key={p.id} className="rounded-lg border border-slate-200 p-2.5 flex items-center gap-2">
                        <div className="w-6 h-6 rounded bg-blue-100 text-blue-600 grid place-items-center"><Target className="w-3 h-3"/></div>
                        <div className="flex-1 min-w-0"><div className="text-[12px] font-semibold text-slate-800">{p.objective || p.title}</div><div className="text-[10px] text-slate-500">{p.tracking_status || p.status} · échéance {fmtDate(p.deadline)}</div></div>
                      </div>
                    ))}
                  </div>
                )}

                {detailTab === "missions" && <div className="italic text-slate-400 text-[11px]">Aucune mission liée pour l'instant.</div>}

                {detailTab === "documents" && (
                  <div><button onClick={()=>nav("/documents")} className="text-[11px] text-blue-600 font-semibold hover:underline">Accéder à l'espace documents →</button></div>
                )}

                {detailTab === "history" && (
                  <div className="text-[11px] text-slate-500 space-y-1">
                    <div>• Créée le {fmtDate(selected.created_at)} par {selected.user_name}</div>
                    <div>• Statut actuel : {(STATUS_META[selected.status]||STATUS_META.completed).label}</div>
                    {selected.duration_min && <div>• Durée : {selected.duration_min} min</div>}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="lg:col-span-6 bg-white rounded-xl border border-slate-200 grid place-items-center p-10">
              <div className="text-center">
                <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
                <div className="text-[13px] text-slate-500">Sélectionnez une intervention pour voir le détail.</div>
              </div>
            </div>
          )}

          {/* Right: quick actions + linked plans + issues + photos */}
          <div className="lg:col-span-3 space-y-3">
            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="ivt-actions">
              <h4 className="text-[12px] font-bold text-slate-900 mb-2">Actions rapides</h4>
              <div className="grid grid-cols-2 gap-1.5">
                <QuickBtn icon={ClipboardCheck} tone="blue"   label="Ajouter un constat"   onClick={()=>toast.info("Constat — fonction à venir")}/>
                <QuickBtn icon={Target}         tone="violet" label="Créer un plan d'action" onClick={()=>nav("/ops/plans?new=1")}/>
                <QuickBtn icon={AlertTriangle}  tone="rose"   label="Signaler un problème" onClick={()=>nav("/ops/alertes?new=1")}/>
                <QuickBtn icon={UserIcon}       tone="blue"   label="Assigner une mission" onClick={()=>toast.info("Missions — utilisez la CTA du hub")}/>
                <QuickBtn icon={Camera}         tone="emerald" label="Ajouter une photo"   onClick={()=>setDetailModal(selected)}/>
                <QuickBtn icon={FileText}       tone="slate"  label="Générer un CR"        onClick={()=>toast.info("Compte rendu — génération PDF à venir")}/>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="ivt-plans-linked">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[12px] font-bold text-slate-900">Plans d'action liés <span className="text-slate-400 font-normal">({linkedPlans.length})</span></h4>
                <button onClick={()=>nav("/ops/plans")} className="text-[10px] text-blue-600 font-semibold hover:underline">Voir tous →</button>
              </div>
              <div className="space-y-1.5 max-h-[180px] overflow-auto">
                {linkedPlans.length === 0 && <div className="italic text-slate-400 text-[10px] text-center py-3">Aucun plan lié.</div>}
                {linkedPlans.slice(0,3).map(p => (
                  <div key={p.id} className="rounded-lg border border-slate-200 p-2 text-[11px]">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500"/>
                      <div className="font-semibold text-slate-800 truncate flex-1">{p.objective || p.title}</div>
                    </div>
                    <div className="text-[10px] text-slate-500">Éch. {fmtDate(p.deadline)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="ivt-issues-linked">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[12px] font-bold text-slate-900">Problèmes détectés <span className="text-slate-400 font-normal">({linkedIssues.length})</span></h4>
                <button onClick={()=>nav("/ops/alertes")} className="text-[10px] text-blue-600 font-semibold hover:underline">Voir tous →</button>
              </div>
              <div className="space-y-1.5 max-h-[160px] overflow-auto">
                {linkedIssues.length === 0 && <div className="italic text-slate-400 text-[10px] text-center py-3">Aucun problème signalé.</div>}
                {linkedIssues.slice(0,3).map(i => (
                  <div key={i.id} className="rounded-lg border border-slate-200 p-2 text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded bg-rose-100 grid place-items-center shrink-0"><AlertTriangle className="w-3 h-3 text-rose-600"/></div>
                      <div className="min-w-0 flex-1"><div className="font-semibold text-slate-800 truncate">{i.title}</div><div className="text-[9px] text-slate-500">Créé le {fmtDate(i.created_at)}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <PhotosBlock selected={selected}/>
          </div>
        </div>
      )}

      {/* Modal (existing form preserved) */}
      <ActivityFormModal open={modalOpen} onClose={()=>setModalOpen(false)} onSaved={load}
                         stores={stores} sectors={sectors} userRole={user?.role}/>
      <MissionAffecteeModal open={missionModalOpen} onClose={()=>setMissionModalOpen(false)} onSaved={load}/>
      {dossierId && <ActivityDossier id={dossierId} onClose={()=>setDossierId(null)} users={users} onChanged={load}/>}
      <ActivityDetailModal activity={detailModal} onClose={()=>setDetailModal(null)}
                           storeName={(id)=>storesById[id]?.canonical_name||storesById[id]?.name||"—"}
                           sectorName={(id)=>sectors.find(x=>x.id===id)?.name||"—"}/>
    </div>
  );
}

function ConstatsView({ selected }) {
  const constats = selected?.payload?.constats || [];
  if (constats.length === 0) return <div className="italic text-slate-400 text-[11px]">Aucun constat saisi.</div>;
  return (
    <div className="space-y-2">
      {constats.map((c,i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-2.5">
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 rounded grid place-items-center shrink-0 ${c.ok?"bg-emerald-100 text-emerald-600":"bg-amber-100 text-amber-600"}`}>{c.ok?<Check className="w-3 h-3"/>:<AlertTriangle className="w-3 h-3"/>}</div>
            <div className="min-w-0"><div className="text-[12px] font-semibold text-slate-800">{c.label}</div><div className="text-[10px] text-slate-500">{c.desc}</div></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function QuickBtn({ icon: Icon, tone, label, onClick }) {
  const toneCls = { blue:"bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100",
                    violet:"bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100",
                    rose:"bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100",
                    emerald:"bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100",
                    slate:"bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100" }[tone] || "bg-slate-50 border-slate-200";
  return (
    <button onClick={onClick} className={`flex items-start gap-1.5 p-2 rounded-lg border text-left ${toneCls}`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
      <span className="text-[10px] font-semibold leading-tight">{label}</span>
    </button>
  );
}

function PhotosBlock({ selected }) {
  const photos = selected?.photos || [];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="ivt-photos">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[12px] font-bold text-slate-900">Photos <span className="text-slate-400 font-normal">({photos.length})</span></h4>
        {photos.length>0 && <button className="text-[10px] text-blue-600 font-semibold hover:underline">Voir toutes →</button>}
      </div>
      {photos.length === 0 && <div className="italic text-slate-400 text-[10px] text-center py-4">Aucune photo pour cette intervention.</div>}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-1">
          {photos.slice(0,5).map((p,i) => (
            <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
              <img src={p} alt="" className="w-full h-full object-cover"/>
            </div>
          ))}
          {photos.length > 5 && (
            <div className="aspect-square rounded-lg bg-slate-800 text-white grid place-items-center text-[13px] font-bold">+{photos.length-5}</div>
          )}
        </div>
      )}
    </div>
  );
}
